import React from 'react'
import { Box, Container, Typography } from '@mui/material'

import type { HolidayCollectionIndexEntry, HolidayPackagesIndexEntry } from '@/src/types/holidays'
import HeaderContainer from '@/src/components/common/header/HeaderRouter'
import MobileHeaderContainer from '@/src/components/common/header/variants/mobile'
import DesktopFooter from '@/src/components/desktop/homepageFooter'
import MobileFooter from '@/src/components/mobile/homepageFooter'
import BottomToolbarWrapper from '@/src/components/mobile/bottomToolbar'
import ErrorBoundaryWrapper from '@/src/containers/common/ErrorBoundary'

import HolidaysPanelWrapper from '@/src/components/common/home/holidaysPanel'
import FeaturedCollectionsWrapper from '@/src/components/common/home/featuredCollections'
import PackageCarouselWrapper from '@/src/components/common/holidays/packageCarousel'
import LobTabsWrapper from '@/src/components/common/home/hotelSearch/lobTabs'
import {
    HOME_HERO_GRADIENT_DESKTOP,
    HOME_HERO_GRADIENT_MOBILE
} from '@/src/components/common/home/common/homeHeroBackground'
import AppTitleBarWrapper from '@/src/components/common/holidays/appTitleBar'
import { HOLIDAYS_MAX_WIDTH } from '@/src/utils/holidays'

interface HolidayIndexPageProps {
    entries: HolidayCollectionIndexEntry[]
    /** Drives the search bar's destination and month options. */
    packages: HolidayPackagesIndexEntry[]
    isMobile: boolean
    domainCountry: string
    /** Inside the native app: its title bar replaces the site header, and there is no footer. */
    isApp: boolean
    appTitle: string
    heading: string
    subheading: string
    tagline: string
    /** Which sibling verticals the strip offers, resolved the same way the home page resolves them. */
    showVisaTab: boolean
    showFlightsHotelsTab: boolean
}

/**
 * The index is a single link list with no device-specific layout, so the two trees differ only in
 * which header and footer they mount. Splitting it into two page files would duplicate the list
 * markup for no behavioural difference — see the device-split note in the surface doc.
 */
const HolidayIndexPage: React.FC<HolidayIndexPageProps> = ({
    entries,
    packages,
    isMobile,
    domainCountry,
    isApp,
    appTitle,
    heading,
    subheading,
    tagline,
    showVisaTab,
    showFlightsHotelsTab
}) => {
    return (
        <Box
            sx={{
                minHeight: '100vh',
                bgcolor: 'neutral.100',
                display: 'flex',
                flexDirection: 'column'
            }}
        >
            <ErrorBoundaryWrapper identifier={`holidays-index-header-${isMobile ? 'mobile' : 'desktop'}`}>
                {isApp && <AppTitleBarWrapper title={appTitle} isRoot />}
                {!isApp && isMobile && <MobileHeaderContainer domainCountry={domainCountry} />}
                {!isApp && !isMobile && <HeaderContainer domainCountry={domainCountry} activeVertical="holidays" />}
            </ErrorBoundaryWrapper>

            {/* Same branded gradient band the hotels landing uses, so the two verticals read as
                one product rather than two sites. */}
            <Box
                component="main"
                sx={{
                    background: { xs: HOME_HERO_GRADIENT_MOBILE, lg: HOME_HERO_GRADIENT_DESKTOP },
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'top center',
                    pb: 2
                }}
            >
                <Container
                    maxWidth={false}
                    sx={{
                        maxWidth: HOLIDAYS_MAX_WIDTH,
                        textAlign: 'center',
                        px: 2,
                        pt: { xs: 1.5, lg: 6 },
                        pb: { xs: 1.5, lg: 0 }
                    }}
                >
                    <Typography
                        component="h1"
                        sx={{
                            mb: { xs: 1.5, md: 2 },
                            fontWeight: 600,
                            fontSize: { xs: '1.75rem', lg: '3.75rem' },
                            color: 'text.primary'
                        }}
                    >
                        {heading}
                    </Typography>
                    <Typography
                        component="h2"
                        sx={{
                            mb: { md: 2 },
                            display: { xs: 'none', md: 'block' },
                            fontWeight: 300,
                            fontSize: 20,
                            color: 'text.primary'
                        }}
                    >
                        {tagline}
                    </Typography>
                    <Typography
                        component="h3"
                        sx={{
                            mb: { md: 2 },
                            display: { xs: 'none', md: 'block' },
                            fontWeight: 300,
                            fontSize: 18,
                            color: 'text.primary'
                        }}
                    >
                        {subheading}
                    </Typography>
                </Container>

                <Container maxWidth={false} sx={{ maxWidth: HOLIDAYS_MAX_WIDTH, px: 2 }}>
                    {!isApp && (
                        <Box sx={{ mt: 3, position: 'relative', zIndex: 2 }}>
                            <ErrorBoundaryWrapper identifier="holidays-index-lobtabs">
                                <LobTabsWrapper
                                    showVisaTab={showVisaTab}
                                    showFlightsHotelsTab={showFlightsHotelsTab}
                                    showHolidaysTab
                                    activeKey="holidays"
                                />
                            </ErrorBoundaryWrapper>
                        </Box>
                    )}

                    <Box
                        sx={{
                            mt: isApp ? 0 : '-12px',
                            position: 'relative',
                            zIndex: 1,
                            bgcolor: 'common.white',
                            px: isMobile ? 2 : 3,
                            pt: isMobile ? 3 : 4,
                            pb: isMobile ? 2.5 : 3,
                            borderRadius: 2,
                            boxShadow: '0px 2px 8px -1px rgba(15, 17, 23, 0.1)'
                        }}
                    >
                        <ErrorBoundaryWrapper identifier="holidays-index-panel">
                            <HolidaysPanelWrapper packages={packages} isMobile={isMobile} />
                        </ErrorBoundaryWrapper>
                    </Box>
                </Container>
            </Box>

            <Container
                maxWidth={false}
                sx={{
                    maxWidth: HOLIDAYS_MAX_WIDTH,
                    px: 2,
                    pt: isMobile ? 3 : 5,
                    // The toolbar is fixed, so the page owes it the height it covers.
                    pb: isMobile && !isApp ? 'calc(72px + env(safe-area-inset-bottom))' : 5
                }}
            >
                <ErrorBoundaryWrapper identifier="holidays-index-carousel">
                    <PackageCarouselWrapper packages={packages} isMobile={isMobile} />
                </ErrorBoundaryWrapper>

                <Box sx={{ mt: isMobile ? 4 : 6 }} />

                <ErrorBoundaryWrapper identifier="holidays-index-featured">
                    <FeaturedCollectionsWrapper
                        collections={entries}
                        packages={packages}
                        sourceSection="holidays_index_featured"
                        isMobile={isMobile}
                    />
                </ErrorBoundaryWrapper>
            </Container>


            {!isApp && (
                <Box sx={{ mt: 'auto' }}>
                    {isMobile ? <MobileFooter showHomeHighlights={false} /> : <DesktopFooter showHomeHighlights={false} />}
                </Box>
            )}

            {/* Holidays is one of the toolbar's own destinations, so the bar belongs here as much
                as on the home page. Hidden in the app, which supplies its own chrome. */}
            {isMobile && !isApp && (
                <ErrorBoundaryWrapper identifier="holidays-index-toolbar">
                    <BottomToolbarWrapper
                        showVisa={showVisaTab}
                        showFlightsHotels={showFlightsHotelsTab}
                        showHolidays
                    />
                </ErrorBoundaryWrapper>
            )}
        </Box>
    )
}

export { HolidayIndexPage }
export default HolidayIndexPage
